/**
 * MRI Phase 1a human RBAC — shared permission contract.
 *
 * Single source of truth for the permission catalog, the three code-defined
 * roles, their permission bundles, and the `effective(...)` resolver. Shared
 * across unified-api (enforcement) and admin-dashboard (UI gating) so both
 * sides reason about the same strings.
 *
 * Roles are NOT stored as permission lists in the DB — only the role name (plus
 * unused-in-1a `extra`/`denied` override columns) lives on `mri_operators`. The
 * bundles below are the authoritative expansion.
 */

// ============================================================================
// Permission catalog
// ============================================================================

export const MRI_PERMISSIONS = {
  ACCESS: 'mri.access',
  CASES_VIEW: 'cases.view',
  CASES_DECIDE: 'cases.decide',
  TASKS_VIEW: 'tasks.view',
  TASKS_CREATE: 'tasks.create',
  TASKS_WORK: 'tasks.work',
  TASKS_ASSIGN: 'tasks.assign',
  TASKS_MANAGE: 'tasks.manage',
  EVIDENCE_VIEW: 'evidence.view',
  EVIDENCE_UPLOAD: 'evidence.upload',
  TYPES_VIEW: 'types.view',
  TYPES_PROMOTE: 'types.promote',
  DRAFTS_MANAGE: 'drafts.manage',
  CONFIG_MANAGE: 'config.manage',
  AGENTS_VIEW: 'agents.view',
  AGENTS_CONTROL: 'agents.control',
  OPERATORS_MANAGE: 'operators.manage',
  AUDIT_VIEW: 'audit.view',
} as const;

export type MriPermission = (typeof MRI_PERMISSIONS)[keyof typeof MRI_PERMISSIONS];

export const MRI_PERMISSION_VALUES = Object.values(MRI_PERMISSIONS) as MriPermission[];

// ============================================================================
// Roles
// ============================================================================

export const MRI_ROLES = ['viewer', 'analyst', 'mri_admin'] as const;

export type MriRole = (typeof MRI_ROLES)[number];

export const MRI_ROLE = {
  VIEWER: 'viewer',
  ANALYST: 'analyst',
  MRI_ADMIN: 'mri_admin',
} as const satisfies Record<string, MriRole>;

// ============================================================================
// Role → permission bundles
// ============================================================================

/**
 * All read/view permissions plus base MRI access. The floor for any seen
 * operator.
 */
const VIEWER_PERMISSIONS: MriPermission[] = [
  MRI_PERMISSIONS.ACCESS,
  MRI_PERMISSIONS.CASES_VIEW,
  MRI_PERMISSIONS.TASKS_VIEW,
  MRI_PERMISSIONS.EVIDENCE_VIEW,
  MRI_PERMISSIONS.TYPES_VIEW,
  MRI_PERMISSIONS.AGENTS_VIEW,
  MRI_PERMISSIONS.AUDIT_VIEW,
];

/**
 * Viewer + the operational verbs an analyst needs to work cases end-to-end.
 * Analysts DO decide cases.
 */
const ANALYST_PERMISSIONS: MriPermission[] = [
  ...VIEWER_PERMISSIONS,
  MRI_PERMISSIONS.TASKS_CREATE,
  MRI_PERMISSIONS.TASKS_WORK,
  MRI_PERMISSIONS.TASKS_ASSIGN,
  MRI_PERMISSIONS.CASES_DECIDE,
  MRI_PERMISSIONS.EVIDENCE_UPLOAD,
];

/**
 * The full catalog — mri_admin is the top role and, by definition, holds every
 * permission. Defined as MRI_PERMISSION_VALUES (not a hand-listed subset) so a
 * newly-added permission auto-grants to admin and can never gate a route that
 * even the highest role cannot reach. On top of the analyst bundle this adds the
 * governance verbs (types.promote, agents.control, operators.manage) plus the
 * admin-config verbs (config.manage, drafts.manage, tasks.manage, tasks.assign).
 */
const MRI_ADMIN_PERMISSIONS: MriPermission[] = [...MRI_PERMISSION_VALUES];

export const MRI_ROLE_PERMISSIONS: Record<MriRole, MriPermission[]> = {
  viewer: VIEWER_PERMISSIONS,
  analyst: ANALYST_PERMISSIONS,
  mri_admin: MRI_ADMIN_PERMISSIONS,
};

// ============================================================================
// Effective-permission resolver
// ============================================================================

/**
 * effective(op) = (MRI_ROLE_PERMISSIONS[role] ∪ extra) \ denied.
 *
 * `extra`/`denied` are per-operator overrides (columns exist but are unused in
 * Phase 1a; passing empty arrays yields the plain role bundle).
 */
export const effectiveMriPermissions = (
  role: MriRole,
  extra: readonly MriPermission[] = [],
  denied: readonly MriPermission[] = [],
): MriPermission[] => {
  const deniedSet = new Set<MriPermission>(denied);
  const granted = new Set<MriPermission>([...MRI_ROLE_PERMISSIONS[role], ...extra]);

  return [...granted].filter(permission => !deniedSet.has(permission));
};
